import {
  Button,
  createListCollection,
  Field as ChakraField,
  Fieldset,
  HStack,
  Input,
  Text,
  VStack,
} from '@chakra-ui/react';
import ErrorsContainer from '@components/common/form/ErrorsContainer';
import { GPSCoordinatesField } from '@components/ui/coordinatesInput';
import { DraggablePanel } from '@components/ui/draggable';
import { Form } from '@components/ui/Form';
import { InputNumber } from '@components/ui/input/InputNumber';
import { CustomSelect, Option } from '@components/ui/select/Select';
import { Switch } from '@components/ui/switch';
import { createToast, ToastStatusEnum } from '@components/ui/toaster';
import { useAppSelector } from '@hooks/redux.hooks';
import useValidation from '@hooks/useValidation';
import { selectActiveSite } from '@redux/authent/authent.selectors';
import { selectHeightSuffix } from '@redux/settings/settings.selectors';
import { useCreatePointsOfInterestMutation } from '@services/config/pointOfInterest.api';
import { eventColor } from '@utils/replay/event.utils';
import { poiSchema } from '@validations/poi.schema';
import { Field, FieldProps, Formik, getIn } from 'formik';
import { FaCircle } from 'react-icons/fa';
import { FormattedMessage, useIntl } from 'react-intl';
import { v4 as uuidv4 } from 'uuid';

import { PointOfInterest, PointOfInterestWithNullableCoordinates } from '@/types/config/pointOfInterest.types';
import { PopupPositionData } from '@/types/popup.types';
import { EventTypeEnum } from '@/types/replay/replay.types';

import { useMapContext } from '../MapContext';
import PopupContainer from './common/PopupContainer';

const formatEventTypeLabel = (eventColor: Record<string, string>) => (option: Option) => (
  <HStack gap={1}>
    <FaCircle color={eventColor[option.value as EventTypeEnum]} />
    <Text textStyle="md">{option.label}</Text>
  </HStack>
);

export default function PoiFormPopup({ top, left, onClose }: Readonly<PopupPositionData>) {
  const { formatMessage } = useIntl();
  const { mapConstraintsRef, mouseSelectedPosition, isSelectionEnabled, setSelectionOn } = useMapContext();
  const showHeightSuffix = useAppSelector(selectHeightSuffix);
  const validationSchema = useValidation(poiSchema);
  const activeSite = useAppSelector(selectActiveSite);
  const [createPoi, { isLoading }] = useCreatePointsOfInterestMutation();
  const defaultPoi: Partial<PointOfInterestWithNullableCoordinates> = {
    name: '',
    code: uuidv4(),
    nature: '',
    site: activeSite?.code,
    active: true,
    height: 0,
    position: {
      latitude: null,
      longitude: null,
      type: '2D',
    },
  };

  function handleCreatePoi(poi: Partial<PointOfInterestWithNullableCoordinates>) {
    createPoi(poi as PointOfInterest)
      .unwrap()
      .then(() => {
        createToast(formatMessage({ id: 'poi.success' }), ToastStatusEnum.SUCCESS);
        onClose();
      });
  }

  return (
    <DraggablePanel.Root constraintsRef={mapConstraintsRef} top={top} left={left}>
      <DraggablePanel.Header onClose={onClose}>
        <FormattedMessage id="poi.creation" />
      </DraggablePanel.Header>
      <DraggablePanel.Body width={{ base: '420px', '4k': '480px' }}>
        <Formik initialValues={defaultPoi} onSubmit={handleCreatePoi} validationSchema={validationSchema}>
          {({ handleSubmit, handleChange, errors, touched, isValid, dirty, values, setFieldValue }) => (
            <Form onSubmit={handleSubmit}>
              <Fieldset.Root
                invalid={
                  (!!errors.nature && touched.nature) ||
                  (!!errors.name && touched.name) ||
                  (!!errors.height && touched.height) ||
                  (!!errors.position && touched.position)
                }
                gap={3}
              >
                <Fieldset.Content width="100%" backgroundColor="neutral.850" flexGrow={1} alignItems="start" gap={0.25}>
                  <PopupContainer>
                    <VStack gap="standardS" width="100%">
                      <ChakraField.Root required invalid={touched.name && !!errors.name}>
                        <ChakraField.Label>
                          <Text pointerEvents="none" color="neutral.300" textStyle="screenStd">
                            <FormattedMessage id="poi.name" />
                          </Text>
                        </ChakraField.Label>
                        <Field as={Input} id="name" name="name" type="text" height="48px" maxLength="50" />
                      </ChakraField.Root>
                      <ChakraField.Root required invalid={touched.nature && !!errors.nature}>
                        <ChakraField.Label>
                          <Text pointerEvents="none" color="neutral.300" textStyle="screenStd">
                            <FormattedMessage id="poi.nature" />
                          </Text>
                        </ChakraField.Label>
                        <CustomSelect
                          width="100%"
                          collection={createListCollection({
                            items: Object.keys(EventTypeEnum).map((key) => ({
                              label: formatMessage({ id: `live.event.types.${key}` }),
                              value: key,
                            })),
                          })}
                          value={values.nature}
                          formatLabel={formatEventTypeLabel(eventColor)}
                          onChange={(value) => value && handleChange('nature')(value)}
                        />
                      </ChakraField.Root>
                    </VStack>
                  </PopupContainer>
                  <PopupContainer>
                    <ChakraField.Root orientation="horizontal">
                      <ChakraField.Label>
                        <Text pointerEvents="none" color="neutral.300" textStyle="screenStd">
                          <FormattedMessage id="poi.active" />
                        </Text>
                      </ChakraField.Label>
                      <Switch
                        size="md"
                        checked={values.active}
                        onCheckedChange={() => {
                          setFieldValue('active', !values.active);
                        }}
                      />
                    </ChakraField.Root>
                  </PopupContainer>
                  <PopupContainer>
                    <ChakraField.Root required invalid={touched.height && !!errors.height}>
                      <ChakraField.Label>
                        <Text pointerEvents="none" color="neutral.300" textStyle="screenStd">
                          <FormattedMessage id="poi.height" />
                        </Text>
                        {showHeightSuffix && (
                          <Text pointerEvents="none" color="neutral.300" textStyle="screenStd">
                            <FormattedMessage id="poi.agl" />
                          </Text>
                        )}
                      </ChakraField.Label>
                      <Field
                        as={InputNumber}
                        id="height"
                        name="height"
                        height="48px"
                        width="96px"
                        suffix="m"
                        onChange={(value?: number) => setFieldValue('height', value ?? null)}
                      />
                    </ChakraField.Root>
                  </PopupContainer>
                  <PopupContainer>
                    <Fieldset.Root invalid={touched.position && !!errors.position}>
                      <Fieldset.Content>
                        <Field name="position">
                          {({ field }: FieldProps<PointOfInterest['position']>) => {
                            const coords = {
                              latitude: field.value.latitude,
                              longitude: field.value.longitude,
                              type: '2D',
                            };
                            return (
                              <GPSCoordinatesField
                                name="position"
                                coordinates={coords}
                                mouseSelectedPosition={mouseSelectedPosition}
                                isSelectionEnabled={isSelectionEnabled}
                                setSelectionOn={setSelectionOn}
                              />
                            );
                          }}
                        </Field>
                      </Fieldset.Content>
                    </Fieldset.Root>
                  </PopupContainer>
                  {Object.keys(errors).some((key) => {
                    const error = getIn(errors, key);
                    const isTouched = getIn(touched, key);
                    if (typeof error === 'object' && error !== null) {
                      return Object.keys(error).some((subKey) => {
                        return isTouched?.[subKey] && !!error?.[subKey];
                      });
                    }
                    return isTouched && !!error;
                  }) && (
                    <PopupContainer>
                      <Fieldset.ErrorText>
                        <ErrorsContainer errors={errors} touched={touched} />
                      </Fieldset.ErrorText>
                    </PopupContainer>
                  )}
                  <PopupContainer>
                    <HStack justify="space-around" width="100%">
                      <Button type="button" onClick={onClose} flex={1} onMouseDown={(e) => e.preventDefault()}>
                        <FormattedMessage id="global.cancel" />
                      </Button>
                      <Button type="submit" disabled={!dirty || !isValid || isLoading} loading={isLoading} flex={1}>
                        <FormattedMessage id="poi.submit" />
                      </Button>
                    </HStack>
                  </PopupContainer>
                </Fieldset.Content>
              </Fieldset.Root>
            </Form>
          )}
        </Formik>
      </DraggablePanel.Body>
    </DraggablePanel.Root>
  );
}
